import React, {Fragment, useEffect, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {
    Bars3Icon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import {useTranslation} from "react-i18next";
import {useStateContext} from "../../contexts/ContextProvider";
import {Navigate, Outlet, useLocation} from "react-router-dom";
import Sidebar, {SidebarContext, SidebarItem} from "./Sidebar";
import SidebarUserMenu from "./SidebarUserMenu";
import {useFacilitySwitchKey} from "../../hooks/useFacilitySwitchKey";
import SidebarContextBlock from "./SidebarContextBlock";
import StaffHotelSearchModal from "./StaffHotelSearchModal";
import FacilitySwitcherModal from "./FacilitySwitcherModal";
import {useAiUsage} from "../../api/useAiUsage";
import axiosClient from "../../api/axios-client";
import {usePermissions} from "../../contexts/PermissionContext";
import ConsumableExhaustedBanner from "../consumables/ConsumableExhaustedBanner";
import TwoFactorGraceBanner from "../twoFactor/TwoFactorGraceBanner";
import LibraryModeBanner from "./LibraryModeBanner";

import PermissionErrorHandler from "../permissions/PermissionErrorHandler";
import PostHogPageViewTracker from "../analytics/PostHogPageViewTracker";
import ChangelogPanel from "../../pages/changelog/ChangelogPanel";
import {useGlobalMessagingNotifications} from "../../hooks/useGlobalMessagingNotifications";
import MustChangePasswordModal from "../feedBack/MustChangePasswordModal";
import CrossTabFacilityChangeModal from "../feedBack/CrossTabFacilityChangeModal";
import SupportChatBubble from "../support/SupportChatBubble";
import useIntercomLauncher from "./useIntercomLauncher";
import NavigationMenu from "./navigation/NavigationMenu";
import {useClientMenuSections} from "./navigation/useClientMenuSections";
import {useStaffMenuSections} from "./navigation/useStaffMenuSections";
import {useSuperAdminMode} from "./navigation/useSuperAdminMode";
import {readLocalStorage, writeLocalStorage} from "../../hooks/useLocalStorageState";


export default function DefaultLayout() {

    const location = useLocation();

    const {token, user, setUser, isUserLoading, isFacilitySwitching, currentFacility} = useStateContext();
    const facilitySwitchKey = useFacilitySwitchKey(currentFacility?.id);
    const {hasPermission} = usePermissions();
    const {t} = useTranslation('navigation');

    const canViewMessaging = hasPermission('messaging.view');
    const canViewChangelog = hasPermission('changelog.view');

    // Notifications temps réel globales (son + badge) via Pusher
    useGlobalMessagingNotifications({
        facilityId: currentFacility?.id ?? user?.facility_id ?? null,
        enabled: canViewMessaging,
    });

    // Mode super admin (dérivé de l'URL) : le menu back-office remplace le menu
    // client. Chaque menu n'interroge ses compteurs que lorsqu'il est affiché.
    const superAdminMode = useSuperAdminMode();
    const clientMenu = useClientMenuSections(!superAdminMode.isOn);
    const staffSections = useStaffMenuSections(superAdminMode.isOn);
    const menuSections = superAdminMode.isOn ? staffSections : clientMenu.sections;
    // Modules et Nouveautés sont des entrées client : absentes du back-office.
    const bottomModules = superAdminMode.isOn ? null : clientMenu.modules;
    const bottomChangelog = superAdminMode.isOn ? null : clientMenu.changelog;

    // AI Usage pour le menu mobile
    const {data: aiUsage} = useAiUsage();

    // État pour le modal de switch facility (mobile)
    const [mobileFacilitySwitcherModalOpen, setMobileFacilitySwitcherModalOpen] = useState(false);

    // Recherche d'hôtel du back-office : menu ou Cmd+K (Ctrl+K), super admin en
    // mode back-office seulement (l'API de recherche est gardée par `super_admin`).
    const [hotelSearchOpen, setHotelSearchOpen] = useState(false);
    const { canSearchHotels } = superAdminMode;
    useEffect(() => {
        if (!canSearchHotels) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setHotelSearchOpen(true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [canSearchHotels]);

    // Barre latérale dépliée : l'état vit ici, la barre le reçoit en prop. Il
    // était auparavant relu dans localStorage toutes les 100 ms.
    const [sidebarExpanded, setSidebarExpanded] = useState(() => readLocalStorage<boolean>('sidebarExpanded', false) === true);
    const toggleSidebar = () => setSidebarExpanded((prev) => {
        writeLocalStorage('sidebarExpanded', !prev);
        return !prev;
    });

    useEffect(() => {
        axiosClient.get('/user')
            .then(({data}) => {
                setUser(data);
            })
    }, []);

    useIntercomLauncher(token, user, location.pathname);

    // useEffect(() => {
    //     if (!token) return; // si pas connecté, on ne fait rien
    //
    //     // Si on est sur une page exclue, on cache ou on shutdown
    //     if (pathname.startsWith('/messaging') || pathname.startsWith('/cardex')) {
    //         shutdown();
    //         hide();
    //     } else {
    //         // Si Intercom n'a jamais été booté, on l'initialise
    //         if (!(window as any).IntercomInitialized) {
    //             Intercom({
    //                 app_id: 'lm7qjndk',
    //                 user_id: user.id.toString(),
    //                 name:    user.name,
    //                 email:   user.email,
    //                 created_at: user.createdAt,
    //                 user_hash:  user.user_hash,
    //             });
    //             (window as any).IntercomInitialized = true;
    //         } else {
    //             // sinon, on met juste à jour les infos et on réaffiche
    //             Intercom({
    //                 app_id: 'lm7qjndk',
    //                 user_id: user.id.toString(), // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
    //                 name: user.name, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
    //                 email: user.email, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
    //                 created_at: user.createdAt, // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
    //                 user_hash: user.user_hash, // HMAC sécurisé
    //             });
    //             // show();
    //         }
    //     }
    // }, [pathname, token, user]);

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [changelogPanelOpen, setChangelogPanelOpen] = useState(false);

    // Après TOUS les hooks : les appeler sous un retour anticipé ferait varier
    // leur nombre d'un rendu à l'autre.
    if (!token) {
        return <Navigate to={"/login"}/>
    }

    return (
        <div className="h-full overflow-hidden">
            <PostHogPageViewTracker />
            <PermissionErrorHandler />
            <MustChangePasswordModal />
            <CrossTabFacilityChangeModal />
            {user?.support_assistant_enabled && <SupportChatBubble />}
            <div className="h-full">

                {/*##################################################*/}
                {/* Début - Barre latérale pour la version mobile*/}
                {/*##################################################*/}
                <Transition.Root show={sidebarOpen} as={Fragment}>
                    <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                        <Transition.Child
                            as={Fragment}
                            enter="transition-opacity ease-linear duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="transition-opacity ease-linear duration-300"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-gray-900/80"/>
                        </Transition.Child>
                        <div className="fixed inset-0 flex">
                            <Transition.Child
                                as={Fragment}
                                enter="transition ease-in-out duration-300 transform"
                                enterFrom="-translate-x-full"
                                enterTo="translate-x-0"
                                leave="transition ease-in-out duration-300 transform"
                                leaveFrom="translate-x-0"
                                leaveTo="-translate-x-full"
                            >
                                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                                    <Transition.Child
                                        as={Fragment}
                                        enter="ease-in-out duration-300"
                                        enterFrom="opacity-0"
                                        enterTo="opacity-100"
                                        leave="ease-in-out duration-300"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                            <button type="button" className="-m-2.5 p-2.5"
                                                    onClick={() => setSidebarOpen(false)}>
                                                <span className="sr-only">{t('sidebar.close_mobile')}</span>
                                                <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true"/>
                                            </button>
                                        </div>
                                    </Transition.Child>
                                    {/* Barre latérale mobile : même menu que la barre bureau (NavigationMenu) */}
                                    <div className="flex grow flex-col overflow-y-auto bg-[#FAFAFA]">
                                        {/* Logo */}
                                        <div className="flex h-14 shrink-0 items-center px-4">
                                            <img
                                                className="h-8 w-auto"
                                                src="/images/logo.svg"
                                                alt="Trigger Flow Logo"
                                            />
                                        </div>

                                        {/* Mode (équipe), puis recherche d'hôtel ou sélecteur d'établissement */}
                                        <SidebarContextBlock
                                            isCompactMode={false}
                                            onNavigate={() => setSidebarOpen(false)}
                                            onOpenFacilitySwitcher={() => setMobileFacilitySwitcherModalOpen(true)}
                                            onOpenHotelSearch={() => setHotelSearchOpen(true)}
                                        />

                                        {/* Menu items */}
                                        <SidebarContext.Provider value={{expanded: true}}>
                                            <div className="flex-1 overflow-y-auto px-2 pt-2">
                                                <ul className="flex flex-col">
                                                    <NavigationMenu
                                                        sections={menuSections}
                                                        onItemClick={() => setSidebarOpen(false)}
                                                    />
                                                </ul>
                                            </div>

                                            {/* Bottom section */}
                                            <div className="flex-none pb-3">
                                                <ul className="px-2">
                                                    {/* Modules */}
                                                    {bottomModules && (
                                                        <SidebarItem
                                                            link={bottomModules.link}
                                                            icon={bottomModules.icon}
                                                            text={bottomModules.text}
                                                            active={location.pathname.startsWith('/modules')}
                                                            onClick={() => setSidebarOpen(false)}
                                                        />
                                                    )}
                                                    {/* Changelog */}
                                                    {bottomChangelog && (
                                                        <div data-changelog-trigger>
                                                            <SidebarItem
                                                                icon={bottomChangelog.icon}
                                                                text={bottomChangelog.text}
                                                                active={changelogPanelOpen}
                                                                badgeCount={bottomChangelog.badgeCount}
                                                                onClick={() => {
                                                                    setSidebarOpen(false);
                                                                    setChangelogPanelOpen(prev => !prev);
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </ul>
                                                <div className="w-full border-t border-gray-200 mt-2 mb-3"></div>
                                                <SidebarUserMenu
                                                    expanded={true}
                                                    facility={currentFacility}
                                                    aiUsage={aiUsage}
                                                />
                                            </div>
                                        </SidebarContext.Provider>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </Dialog>
                </Transition.Root>
                {/*##################################################*/}
                {/* Fin - Barre latérale pour la version mobile*/}
                {/*##################################################*/}
                {/*##################################################*/}

                {/*##################################################*/}
                {/* Début - Barre latérale pour la version desktop */}
                {/*##################################################*/}
                <Sidebar
                    expanded={sidebarExpanded}
                    onToggle={toggleSidebar}
                    onOpenHotelSearch={() => setHotelSearchOpen(true)}
                    bottomItems={
                        <>
                            {bottomModules && (
                                <SidebarItem
                                    link={bottomModules.link}
                                    icon={bottomModules.icon}
                                    text={bottomModules.text}
                                    active={location.pathname.startsWith('/modules')}
                                />
                            )}
                            {bottomChangelog && (
                                <div data-changelog-trigger>
                                    <SidebarItem
                                        icon={bottomChangelog.icon}
                                        text={bottomChangelog.text}
                                        active={changelogPanelOpen}
                                        badgeCount={bottomChangelog.badgeCount}
                                        onClick={() => setChangelogPanelOpen(prev => !prev)}
                                    />
                                </div>
                            )}
                        </>
                    }
                >
                    <NavigationMenu sections={menuSections} />
                </Sidebar>
                {/*##################################################*/}
                {/* Fin - Barre latérale pour la version desktop */}
                {/*##################################################*/}


                {/*##################################################*/}
                {/* Début - En-tête de la version mobile */}
                {/*##################################################*/}
                <div
                    className="fixed w-full min-h-16 top-0 z-30 flex items-center justify-between gap-x-6 px-4 py-2 sm:px-6 lg:hidden pointer-events-none">
                    <button type="button" className="-m-2.5 p-2.5 lg:hidden pointer-events-auto"
                            onClick={() => setSidebarOpen(true)}>
                        <span className="sr-only">{t('sidebar.open_mobile')}</span>
                        <Bars3Icon className="h-6 w-6" aria-hidden="true"/>
                    </button>
                    {/*<div className="flex-1 text-sm font-normal leading-6">{displayPath}</div>*/}
                    {/*<a href="#">*/}
                    {/*    <img*/}
                    {/*        className="h-8 w-8 rounded-full bg-gray-50"*/}
                    {/*        src="/images/blank.png"*/}
                    {/*        alt=""*/}
                    {/*    />*/}
                    {/*</a>*/}
                </div>
                {/*##################################################*/}
                {/* Fin - En-tête de la version mobile */}
                {/*##################################################*/}

                <main className={`h-full transition-all duration-300 ease-out overflow-hidden ${sidebarExpanded ? 'lg:ml-60' : 'lg:ml-[62px]'}`}>
                    <div className="h-full overflow-y-auto">

                        {/* Interruption de service en cours : non fermable, il
                            disparaît quand le solde remonte (ADR 0005). */}
                        <ConsumableExhaustedBanner/>

                        <TwoFactorGraceBanner/>

                        {/* Super admin sur l'hôtel bibliothèque de modèles. */}
                        <LibraryModeBanner/>

                        {/* Indexé sur l'établissement : une bascule remonte la page, pour
                            qu'aucun état local de l'hôtel précédent ne survive. */}
                        <Outlet key={facilitySwitchKey}/>

                    </div>
                </main>

                {/* Overlay de chargement lors du changement d'établissement */}
                {isFacilitySwitching && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm">
                        <div className="flex flex-col items-center gap-3">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-main-color"></div>
                            <p className="text-sm font-medium text-gray-600">{t('facility_switcher.switching')}</p>
                        </div>
                    </div>
                )}


            </div>

            {/* Changelog slide panel */}
            {canViewChangelog && (
                <ChangelogPanel
                    isOpen={changelogPanelOpen}
                    onClose={() => setChangelogPanelOpen(false)}
                />
            )}

            {/* Modal de switch facility (mobile) */}
            {canSearchHotels && <StaffHotelSearchModal open={hotelSearchOpen} setOpen={setHotelSearchOpen}/>}
            <FacilitySwitcherModal
                open={mobileFacilitySwitcherModalOpen}
                setOpen={setMobileFacilitySwitcherModalOpen}
            />
        </div>
    )
}
